import { apiService } from "./api";
import { NOTIFICATION_SERVICE_PATH } from "@/config";

export interface SegmentResponseDTO {
  id?: string;
  _id?: string;
  name: string;
  country_operation?: string;
  program_id?: string;
  roles_in_program?: string[];
  badge_ids?: string[];
  created_by?: string;
  created_by_name?: string;
  users?: number;
  user_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface GetSegmentsResponse {
  status: boolean;
  message: string;
  data: SegmentResponseDTO[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

export interface CreateSegmentPayload {
  name: string;
  country_operation: string;
  program_id: string;
  roles_in_program: string[];
  badge_ids: string[];
  created_by: string;
}

export interface CampaignFormat {
  fontSize?: string;
  color?: string;
  backgroundColor?: string;
  textColor?: string;
  fontStyle?: string;
  alignment?: string;
}

export interface CampaignContent {
  heading?: string;
  description?: string;
}

export interface CampaignResponseDTO {
  id?: string;
  _id?: string;
  feedback_id?: string;
  recipient_role_id?: string;
  program_id?: string;
  list_id?: string;
  campaign_name?: string;
  campaignName?: string;
  notification_type?: string;
  label?: string;
  notificationType?: string;
  notificationTemplate?: string;
  notification_template?: string | null;
  audienceList?: string[] | string;
  audience_list?: string[] | string;
  triggers?: string;
  messageType?: string;
  messageTitle?: string;
  smsContent?: string;
  message_type?: string;
  message_title_format?: CampaignFormat;
  message_body_format?: CampaignFormat;
  image_url?: string;
  cta_format?: CampaignFormat;
  in_app_contact?: {
    heading: string;
    description: string;
  };
  message_title?: string;
  messageTitleFormat?: CampaignFormat;
  messageBody?: string;
  message_body?: string;
  messageBodyFormat?: CampaignFormat;
  imageUrl?: string;
  ctaButtonText?: string;
  cta_button_text?: string;
  ctaUrl?: string;
  cta_url?: string;
  ctaFormat?: CampaignFormat;
  cta_background_color?: string;
  in_app_content?: CampaignContent;
  sms_content?: string;
  schedule_start_time?: string;
  schedule_end_time?: string;
  approval_status?: string;
  reminder_frequency?: string;
  fallback_flag?: number;
  status?: string;
  createdBy?: string;
  created_by?: string;
  created_by_name?: string;
  createdByName?: string;
  updated_by?: string;
  updated_by_name?: string;
  updatedByName?: string;
  created_at?: string;
  updated_at?: string;
    activity_logs?: {
    user_id?: string;
    timestamp?: string;
    action?: string;
  }[];
}

export interface CampaignCountResponseDTO {
  total?: number;
  sent?: number;
  draft?: number;
  scheduled?: number;
  pending_approval?: number;
}

export interface CampaignMetricsDTO {
  send_volume?: number;
  delivery_rate?: number;
  open_rate?: number;
  cta_success_rate?: number;
  sms_fallback_triggered?: number;
  sms_delivered?: number;
  average_time_to_delivery_minutes?: string;
}

export interface CampaignMetricsResponseDTO {
  status?: boolean;
  message?: string;
  data?: CampaignMetricsDTO;
}

export interface GetCampaignsResponse {
  status: boolean;
  message: string;
  data: CampaignResponseDTO[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

export type CreateCampaignPayload = Omit<CampaignResponseDTO, "id" | "_id">;

export async function createSegment(payload: CreateSegmentPayload) {
  const response = await apiService({
    url: "/segments",
    method: "post",
    protectedRoute: true,
    requestData: payload,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response;
}

export async function getSegments({
  page = 1,
  limit = 10,
  search = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
}) {
  const params: Record<string, any> = { page, limit };
  if (search) {
    params.search = search;
  }

  const response = await apiService({
    url: "/segments",
    method: "get",
    protectedRoute: true,
    params,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response as GetSegmentsResponse;
}

export async function getSegmentById(segmentId: string) {
  const response = await apiService({
    url: `/segments/${segmentId}`,
    method: "get",
    protectedRoute: true,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response as { data: SegmentResponseDTO };
}

export async function updateSegment(segmentId: string, payload: Partial<CreateSegmentPayload>) {
  const response = await apiService({
    url: `/segments/${segmentId}`,
    method: "put",
    protectedRoute: true,
    requestData: payload,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response;
}

export async function createCampaign(payload: CreateCampaignPayload) {
  const response = await apiService({
    url: "/campaigns",
    method: "post",
    protectedRoute: true,
    requestData: payload,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response as { data?: CampaignResponseDTO };
}

export async function getCampaigns({
  page = 1,
  limit = 10,
  search = "",
  exclude_status = "",
}: {
  page?: number;
  limit?: number;
  search?: string;
  exclude_status?: string;
}) {
  const params: Record<string, any> = { page, limit };
  if (search) {
    params.search = search;
  }
  if (exclude_status) {
    params.exclude_status = exclude_status;
  }

  const response = await apiService({
    url: "/campaigns",
    method: "get",
    protectedRoute: true,
    params,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response as GetCampaignsResponse;
}

export async function getCampaignById(campaignId: string) {
  const response = await apiService({
    url: `/campaigns/${campaignId}`,
    method: "get",
    protectedRoute: true,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response as { data: CampaignResponseDTO };
}

export async function updateCampaign(campaignId: string, payload: Partial<CreateCampaignPayload>) {
  const response = await apiService({
    url: `/campaigns/${campaignId}`,
    method: "patch",
    protectedRoute: true,
    requestData: payload,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response;
}

export async function deleteCampaign(campaignId: string) {
  const response = await apiService({
    url: `/campaigns/${campaignId}`,
    method: "delete",
    protectedRoute: true,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response;
}

export async function updateCampaignApprovalStatus(campaignId: string, status: string) {
  const response = await apiService({
    url: `/campaigns/${campaignId}/approval-status`,
    method: "patch",
    protectedRoute: true,
    requestData: { approval_status: status },
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response;
}

export async function getCampaignCount() {
  const response = await apiService({
    url: `/campaigns/counts`,
    method: "get",
    protectedRoute: true,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response as { data: CampaignCountResponseDTO };
}

export async function getCampaignMetrics(campaignId: string) {
  const response = apiService({
    url: `/campaigns/${campaignId}/metrics`,
    method: "get",
    protectedRoute: true,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });
  return response as CampaignMetricsResponseDTO;
}
export async function getDeliveryEvents({
  id = "",
  page = 1,
  limit = 10,
  search = "",
}: {
  id?: string;
  page?: number;
  limit?: number;
  search?: string;
}) {
  const params: Record<string, any> = { page, limit };
  if (search) {
    params.search = search;
  }

  const response = await apiService({
    url: `/campaigns/${id}/recipient-metrics`,
    method: "get",
    protectedRoute: true,
    params,
    servicePath: NOTIFICATION_SERVICE_PATH,
  });

  return response;
}
